import { db, storage, auth } from '../firebase';
import { getOrCreateSupportConversation } from './chat';
import { ensureCoachSlug } from './coachSlug';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, getDocFromServer, increment, runTransaction } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import type {
  UserProfile,
  AccountType,
  SkillLevel,
  CoachSpecialty,
  ProfileMode,
  PlayerProfile,
  CoachProfile,
  CoachVerificationStatus,
  UpdatePlayerProfileData,
  UpdateCoachProfileData,
} from '../types/user';
import { MAX_VIDEO_SIZE } from '../config/constants';

// ============================================
// Types for Profile Creation/Update
// ============================================

export interface CreateProfileData {
  uid: string;
  email: string;
  displayName?: string;
  accountType: AccountType;
  bio?: string | null;
  skillLevel?: SkillLevel | null;
  location?: string | null;
  tennisBackground?: string | null;
  emailVerified?: boolean;
  termsAcceptedAt?: string;
  // Legal acceptance tracking (versioned)
  acceptedPlayerTermsVersion?: number;
  acceptedCoachTermsVersion?: number;
  acceptedPrivacyVersion?: number;
  playerTermsAcceptedAt?: string;
  coachTermsAcceptedAt?: string;
  privacyAcceptedAt?: string;
}

export interface UpdateProfileData {
  displayName?: string;
  bio?: string | null;
  avatarUrl?: string | null;
  skillLevel?: SkillLevel | null;
  location?: string | null;
  tennisBackground?: string | null;
  currentMode?: AccountType;
  shortDescriptor?: string;
  achievements?: string[];
  specialties?: CoachSpecialty[];
  sampleVideoUrl?: string;
  experience?: number;
  utr?: number;
  worksWithLevels?: SkillLevel[];
  username?: string;
  termsAcceptedAt?: string;
  // Legal acceptance tracking (versioned)
  acceptedPlayerTermsVersion?: number;
  acceptedCoachTermsVersion?: number;
  acceptedPrivacyVersion?: number;
  playerTermsAcceptedAt?: string;
  coachTermsAcceptedAt?: string;
  privacyAcceptedAt?: string;
  notificationPreferences?: {
    follows?: boolean;
    likes?: boolean;
    comments?: boolean;
    messages?: boolean;
    lessonRequests?: boolean;
    lessonUpdates?: boolean;
  };
}

// ============================================
// User Profile CRUD (with new embedded structure)
// ============================================

/**
 * Creates a new user profile in Firestore with embedded player/coach profiles.
 * Based on accountType selection:
 * - 'player': Creates populated playerProfile, no coachProfile
 * - 'coach': Creates empty playerProfile, populated coachProfile with pending verification
 */
export async function createUserProfile(data: CreateProfileData): Promise<void> {
  const userRef = doc(db, 'users', data.uid);
  // No placeholder. Email signup supplies no name (Signup.tsx has no name
  // field by design; onboarding step 1 collects it), and stamping the literal
  // 'User' here meant that string was the player's stored name until they
  // finished onboarding, and prefilled the very field meant to collect it.
  // Read-time fallbacks already cover a blank name for display.
  const displayName = data.displayName ?? '';

  // Base player profile
  const playerProfile: PlayerProfile = {
    displayName,
    bio: data?.bio ?? null,
    skillLevel: data?.skillLevel ?? null,
    location: data?.location ?? null,
    tennisBackground: data?.tennisBackground ?? null,
    shotConsultCredits: 1,
  };

  // Build the profile document.
  // accountType 'parent' is a player-side account holder (the parent) whose
  // role/mode are 'player' (they use the player surface to manage the youth
  // profile). 'coach' has its own coach mode. ProfileMode is only
  // 'coach' | 'player', so we explicitly map 'parent' → 'player' here rather
  // than relying on a cast.
  const role: 'coach' | 'player' = data.accountType === 'coach' ? 'coach' : 'player';
  const currentMode: ProfileMode = data.accountType === 'coach' ? 'coach' : 'player';
  const profile: Record<string, unknown> = {
    uid: data.uid,
    email: data.email,
    role,
    currentMode,
    emailVerified: data.emailVerified ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    // Legacy field for backward compatibility
    accountType: data.accountType,
    displayName, // Legacy root-level displayName
  };

  // Legal acceptance — persist any provided fields
  const legalFields = [
    'termsAcceptedAt',
    'acceptedPlayerTermsVersion',
    'acceptedCoachTermsVersion',
    'acceptedPrivacyVersion',
    'playerTermsAcceptedAt',
    'coachTermsAcceptedAt',
    'privacyAcceptedAt',
  ] as const;
  for (const field of legalFields) {
    const value = data[field];
    if (value !== undefined) {
      profile[field] = value;
    }
  }

  if (data.accountType === 'coach') {
    // Coach signup: create empty player profile and initial coach profile
    profile.playerProfile = {
      displayName,
    };
    profile.coachProfile = {
      displayName,
      bio: data?.bio ?? null,
      verificationStatus: 'none' as CoachVerificationStatus, // Will be set to 'pending' after onboarding
    };
  } else {
    // 'player' or 'parent' — both populate the playerProfile. For parent
    // accounts the playerProfile represents the youth profile; the parent
    // identity (parentFirstName/parentLastName) lives at the root level.
    // verifyParentWithCard updates playerProfile.displayName + tennis fields
    // later in the parent signup flow.
    profile.playerProfile = playerProfile;
  }

  await setDoc(userRef, profile);

  // Create the pinned Double Bagel support conversation (with welcome message) for this new user.
  // Awaited so the welcome message is present before the user reaches their first page.
  await getOrCreateSupportConversation(data.uid, displayName).catch(err =>
    console.warn('[createUserProfile] Could not create support conversation:', err)
  );
}

/**
 * Gets a user profile by userId.
 * Returns the full UserProfile with both legacy fields and new embedded profiles.
 */
export async function getUserProfile(userId: string, forceServer: boolean = false): Promise<UserProfile | null> {
  const userRef = doc(db, 'users', userId);
  // Use getDocFromServer when we need fresh data (e.g., after a write operation)
  const userSnap = forceServer ? await getDocFromServer(userRef) : await getDoc(userRef);

  if (!userSnap.exists()) {
    return null;
  }

  const data = userSnap.data();

  // Build UserProfile with backward compatibility
  const profile: UserProfile = {
    uid: data.uid,
    email: data.email,
    accountType: data.accountType || data.currentMode || 'player',
    currentMode: data.currentMode || data.accountType,

    // Legacy root-level fields (for backward compatibility)
    displayName: data.displayName,
    username: data.username,
    bio: data.bio,
    avatarUrl: data.avatarUrl,
    skillLevel: data.skillLevel,
    location: data.location,
    tennisBackground: data.tennisBackground,
    utr: data.utr,

    // Coach-specific legacy fields
    isVerified: data.isVerified ?? data.coachProfile?.verificationStatus === 'verified',
    shortDescriptor: data.shortDescriptor ?? data.coachProfile?.shortDescriptor,
    achievements: data.achievements ?? data.coachProfile?.achievements,
    specialties: data.specialties ?? data.coachProfile?.specialties,
    sampleVideoUrl: data.sampleVideoUrl ?? data.coachProfile?.sampleVideoUrl,
    experience: data.experience ?? data.coachProfile?.experience,
    worksWithLevels: data.worksWithLevels ?? data.coachProfile?.worksWithLevels,
    averageReviewRating: data.averageReviewRating ?? data.coachProfile?.averageReviewRating,
    totalReviews: data.totalReviews ?? data.coachProfile?.totalReviews,

    // New embedded profiles
    playerProfile: data.playerProfile,
    coachProfile: data.coachProfile,

    // Email verification
    emailVerified: data.emailVerified,

    // Role
    role: data.role,

    isActive: data.isActive,

    // Referral attribution
    referredBy: data.referredBy,

    // Notifications
    fcmToken: data.fcmToken,
    notificationPreferences: data.notificationPreferences,

    // Credits
    creditBalanceCents: data.creditBalanceCents,

    // Social
    followersCount: data.followersCount ?? data.playerProfile?.followersCount,
    followingCount: data.followingCount ?? data.playerProfile?.followingCount,

    // Metadata
    createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate?.().toISOString() || new Date().toISOString(),
  };

  // Lazy slug backfill — runs once for verified coaches that pre-date the slug feature.
  // Requires an authenticated caller so the Firestore write is permitted.
  // Fire-and-forget: after the slug is written the condition is never true again.
  if (
    profile.coachProfile?.verificationStatus === 'verified' &&
    !profile.coachProfile?.slug &&
    auth.currentUser !== null
  ) {
    ensureCoachSlugForUser(userId).catch((err: unknown) =>
      console.warn('[getUserProfile] slug backfill failed:', err)
    );
  }

  return profile;
}

/**
 * Updates a user profile (legacy function for backward compatibility).
 * Routes updates to the appropriate embedded profile based on field names.
 */
export async function updateUserProfile(
  userId: string,
  data: UpdateProfileData
): Promise<void> {
  const userRef = doc(db, 'users', userId);
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  // Copy all fields to root level for backward compatibility
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined) {
      updates[key] = value;
    }
  });

  await updateDoc(userRef, updates);

  // Update participant details in conversations if name or avatar changed
  const conversationUpdates: { name?: string; avatarUrl?: string | null } = {};
  if (data.displayName !== undefined) {
    conversationUpdates.name = data.displayName;
  }
  if (data.avatarUrl !== undefined) {
    conversationUpdates.avatarUrl = data.avatarUrl;
  }

  if (Object.keys(conversationUpdates).length > 0) {
    const { updateParticipantDetailsInConversations } = await import('./chat');
    await updateParticipantDetailsInConversations(userId, conversationUpdates);
  }
}

/**
 * Flip a single notification preference.
 *
 * Dot-notation is the whole point. Firestore's `updateDoc` MERGES when given a
 * field path like `notificationPreferences.promotions`, but REPLACES the entire
 * map when given an object under `notificationPreferences`. The account screen
 * used the second form, rebuilt from a hardcoded list of six legacy keys, so
 * every flip silently deleted any preference outside that list.
 */
export async function updateNotificationPreference(
  userId: string,
  key: string,
  value: boolean
): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    [`notificationPreferences.${key}`]: value,
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// Player Profile Functions
// ============================================

/**
 * Gets just the player profile for a user
 */
export async function getPlayerProfile(userId: string): Promise<PlayerProfile | null> {
  const profile = await getUserProfile(userId);
  return profile?.playerProfile || null;
}

/**
 * Updates the player profile
 */
export async function updatePlayerProfile(
  userId: string,
  data: UpdatePlayerProfileData
): Promise<void> {
  const userRef = doc(db, 'users', userId);

  // Build dot-notation updates for nested playerProfile fields
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined) {
      updates[`playerProfile.${key}`] = value;
      // Mirror a handful of fields at root level for legacy readers (displayName, avatarUrl)
      // bio is intentionally excluded — playerProfile.bio is the single source of truth
      if (['displayName', 'avatarUrl', 'skillLevel', 'location', 'tennisBackground', 'utr', 'username'].includes(key)) {
        updates[key] = value;
      }
    }
  });

  await updateDoc(userRef, updates);

  // Update conversations if name or avatar changed
  if (data.displayName !== undefined || data.avatarUrl !== undefined) {
    const profile = await getUserProfile(userId);
    // Only update conversations if this is the active profile
    if (profile?.currentMode === 'player') {
      const conversationUpdates: { name?: string; avatarUrl?: string | null } = {};
      if (data.displayName !== undefined) conversationUpdates.name = data.displayName;
      if (data.avatarUrl !== undefined) conversationUpdates.avatarUrl = data.avatarUrl;

      if (Object.keys(conversationUpdates).length > 0) {
        const { updateParticipantDetailsInConversations } = await import('./chat');
        await updateParticipantDetailsInConversations(userId, conversationUpdates);
      }
    }
  }
}

// ============================================
// Coach Profile Functions
// ============================================

/**
 * Gets just the coach profile for a user
 */
export async function getCoachProfile(userId: string): Promise<CoachProfile | null> {
  const profile = await getUserProfile(userId);
  return profile?.coachProfile || null;
}

/**
 * Initializes an empty coach profile for a player applying to become a coach.
 * Sets verificationStatus to 'none' - will be updated to 'pending' after onboarding.
 */
export async function initializeCoachProfile(userId: string): Promise<void> {
  const profile = await getUserProfile(userId);
  if (!profile) {
    throw new Error('User profile not found');
  }

  if (profile.coachProfile) {
    throw new Error('Coach profile already exists');
  }

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    coachProfile: {
      displayName: profile.playerProfile?.displayName || profile.displayName || 'Coach',
      verificationStatus: 'none' as CoachVerificationStatus,
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Decrements the player's global shot consult credit by 1 (min 0).
 * Called once when a player submits their first Shot Consult with any coach.
 */
export async function markShotConsultUsed(userId: string, _coachId: string): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    const current = (snap.data()?.playerProfile?.shotConsultCredits ?? 0) as number;
    if (current <= 0) return; // already exhausted — don't go negative
    tx.update(userRef, {
      'playerProfile.shotConsultCredits': increment(-1),
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Ensures the coach has a slug registered in `coachSlugs` and stored on their profile.
 * Reads the current profile, generates a slug if needed, and writes it back.
 * No-op if the coach already has a valid slug.
 */
async function ensureCoachSlugForUser(userId: string): Promise<void> {
  // Use getDoc directly (not getUserProfile) to avoid a recursive trigger:
  // getUserProfile fires ensureCoachSlugForUser, which would call getUserProfile
  // again before the slug is written — causing multiple concurrent slug claims.
  const snap = await getDoc(doc(db, 'users', userId));
  const cp = (snap.data() as UserProfile | undefined)?.coachProfile;
  if (!cp?.displayName) return;

  const slug = await ensureCoachSlug(
    userId,
    cp.slug,
    cp.displayName,
    cp.shortDescriptor,
    cp.specialties as string[] | undefined,
  );

  if (slug && slug !== cp.slug) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { 'coachProfile.slug': slug, updatedAt: serverTimestamp() });
  }
}

/**
 * Public version of ensureCoachSlugForUser that accepts already-fetched coach data,
 * avoiding a redundant Firestore read. Returns the slug (new or existing), or null.
 *
 * Skips silently if the user is not authenticated or the coach already has a slug.
 */
export async function backfillCoachSlug(
  userId: string,
  coachProfile: import('../types/user').CoachProfile,
): Promise<string | null> {
  if (!auth.currentUser) return null;
  if (coachProfile.slug) return coachProfile.slug;
  if (!coachProfile.displayName) return null;

  const slug = await ensureCoachSlug(
    userId,
    undefined,
    coachProfile.displayName,
    coachProfile.shortDescriptor,
    coachProfile.specialties as string[] | undefined,
  );

  if (slug) {
    // Best-effort write back to the user doc. May fail if caller is not the
    // coach (Firestore owner check), but the slug is already registered in
    // coachSlugs — so we still return it for use in the current session.
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 'coachProfile.slug': slug, updatedAt: serverTimestamp() });
    } catch {
      // non-fatal
    }
  }
  return slug ?? null;
}

/**
 * Updates the coach profile
 */
export async function updateCoachProfile(
  userId: string,
  data: UpdateCoachProfileData
): Promise<void> {
  const userRef = doc(db, 'users', userId);

  // Build dot-notation updates for nested coachProfile fields
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined) {
      updates[`coachProfile.${key}`] = value;
      // Mirror a handful of fields at root level for legacy readers (displayName, avatarUrl)
      // bio is intentionally excluded — coachProfile.bio is the single source of truth
      if (['displayName', 'avatarUrl', 'location', 'shortDescriptor', 'achievements',
           'specialties', 'sampleVideoUrl', 'experience', 'utr', 'worksWithLevels', 'username'].includes(key)) {
        updates[key] = value;
      }
    }
  });

  await updateDoc(userRef, updates);

  // Update conversations if name or avatar changed
  if (data.displayName !== undefined || data.avatarUrl !== undefined) {
    const profile = await getUserProfile(userId);
    // Only update conversations if this is the active profile
    if (profile?.currentMode === 'coach') {
      const conversationUpdates: { name?: string; avatarUrl?: string | null } = {};
      if (data.displayName !== undefined) conversationUpdates.name = data.displayName;
      if (data.avatarUrl !== undefined) conversationUpdates.avatarUrl = data.avatarUrl;

      if (Object.keys(conversationUpdates).length > 0) {
        const { updateParticipantDetailsInConversations } = await import('./chat');
        await updateParticipantDetailsInConversations(userId, conversationUpdates);
      }
    }
  }

  // Ensure the coach has a public slug — generate one if missing (fire-and-forget)
  ensureCoachSlugForUser(userId).catch((err: unknown) =>
    console.warn('[updateCoachProfile] slug generation failed:', err)
  );
}

/**
 * Requests coach verification - sets status to 'pending'
 * Called after coach completes onboarding
 */
export async function requestCoachVerification(userId: string): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    'coachProfile.verificationStatus': 'pending' as CoachVerificationStatus,
    'coachProfile.verificationRequestedAt': new Date().toISOString(),
    // Switch to player mode while awaiting coach verification
    currentMode: 'player' as ProfileMode,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Completes coach onboarding for invitation-only coaches.
 * Sets verificationStatus to 'verified' immediately (no admin review required — coach was pre-vetted via invite).
 * Keeps currentMode as 'coach' so they land on the coach dashboard.
 * Called from step-3's Go-live submit handler at the end of onboarding.
 */
export async function completeCoachOnboarding(userId: string): Promise<void> {
  const userRef = doc(db, 'users', userId);
  const now = new Date().toISOString();
  await updateDoc(userRef, {
    'coachProfile.verificationStatus': 'verified' as CoachVerificationStatus,
    'coachProfile.verifiedAt': now,
    currentMode: 'coach' as ProfileMode,
    updatedAt: serverTimestamp(),
  });

  // Generate public slug now that the coach is verified
  ensureCoachSlugForUser(userId).catch((err: unknown) =>
    console.warn('[completeCoachOnboarding] slug generation failed:', err)
  );
}

// ============================================
// Profile Mode Switching
// ============================================

/**
 * Switches the user's active profile mode.
 * Users can freely switch between modes - the UI will handle showing
 * disabled/read-only state for unverified coach profiles.
 */
export async function switchProfileMode(userId: string, mode: ProfileMode): Promise<ProfileMode> {
  const profile = await getUserProfile(userId);

  if (!profile) {
    throw new Error('User profile not found');
  }

  // Check if switching to coach mode - only require that the profile exists
  if (mode === 'coach') {
    if (!profile.coachProfile) {
      throw new Error('No coach profile exists. Please apply to become a coach first.');
    }
    // Allow switching even if not verified - UI will show read-only state
  }

  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    currentMode: mode,
    // Update legacy field too
    accountType: mode,
    updatedAt: serverTimestamp(),
  });

  return mode;
}

// ============================================
// Avatar Upload Functions
// ============================================

/**
 * Uploads an avatar for the player profile
 */
export async function uploadPlayerAvatar(
  userId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  const timestamp = Date.now();
  const fileName = `player-avatar-${timestamp}.${file.name.split('.').pop()}`;
  const storageRef = ref(storage, `users/${userId}/avatars/${fileName}`);

  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Update player profile with avatar URL
          await updatePlayerProfile(userId, { avatarUrl: downloadURL });

          resolve(downloadURL);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

/**
 * Uploads an avatar for the coach profile
 */
export async function uploadCoachAvatar(
  userId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  const timestamp = Date.now();
  const fileName = `coach-avatar-${timestamp}.${file.name.split('.').pop()}`;
  const storageRef = ref(storage, `users/${userId}/avatars/${fileName}`);

  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Update coach profile with avatar URL
          await updateCoachProfile(userId, { avatarUrl: downloadURL });

          resolve(downloadURL);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

/**
 * Downscale an image to at most `maxWidth` before upload.
 *
 * The hero renders full-bleed on the public profile, so an unresized phone
 * photo — routinely 8 to 12 MB at 4000px — would be shipped to every visitor.
 * The four bundled surfaces are 2400 wide at 152 to 644 KB, so that is the
 * target. Nothing else in this app resizes on upload; an avatar renders at
 * 88px so nobody had to care before.
 *
 * WebP first, JPEG as the fallback: `toBlob` hands back null for a type the
 * browser cannot encode, which is the signal to retry.
 */
async function downscaleImage(file: File, maxWidth = 2400): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  // Already small enough: keep the original bytes rather than re-encoding and
  // losing quality for nothing.
  if (bitmap.width <= maxWidth) { bitmap.close(); return file; }

  const scale = maxWidth / bitmap.width;
  const canvas = document.createElement('canvas');
  canvas.width = maxWidth;
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) { bitmap.close(); return file; }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const encode = (type: string) =>
    new Promise<Blob | null>((res) => canvas.toBlob(res, type, 0.85));
  return (await encode('image/webp')) ?? (await encode('image/jpeg')) ?? file;
}

/**
 * Uploads a coach's custom profile-hero image.
 *
 * Its own `hero/` prefix rather than `avatars/`: the avatar rule allows any
 * authenticated user to write to any user's folder, and a public hero is a
 * worse thing to leave open than an avatar. See storage.rules.
 *
 * Does NOT write the profile field — the caller decides whether the upload
 * becomes the active selection, so a coach can upload and still be looking at
 * a court surface.
 */
export async function uploadCoachHero(
  userId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  const resized = await downscaleImage(file);
  const ext = resized.type === 'image/webp' ? 'webp' : 'jpg';
  const storageRef = ref(storage, `users/${userId}/hero/hero-${Date.now()}.${ext}`);
  const uploadTask = uploadBytesResumable(storageRef, resized);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => onProgress?.((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
      reject,
      async () => {
        try {
          resolve(await getDownloadURL(uploadTask.snapshot.ref));
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

// ============================================
// Sample Video Upload (Coach Profile)
// ============================================

/**
 * Uploads a sample video for a coach profile
 */
export async function uploadSampleVideo(
  userId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  if (!file.type.startsWith('video/')) {
    throw new Error('File must be a video');
  }

  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error(`Video size must be less than ${Math.round(MAX_VIDEO_SIZE / 1024 / 1024)}MB`);
  }

  const fileName = 'sample-video.mp4';
  const storageRef = ref(storage, `users/${userId}/sample-videos/${fileName}`);

  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        reject(error);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

        // Update both root level and coach profile
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          sampleVideoUrl: downloadURL,
          'coachProfile.sampleVideoUrl': downloadURL,
          updatedAt: serverTimestamp(),
        });

        resolve(downloadURL);
      }
    );
  });
}
